import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChatBubbleLeftIcon,
  EyeIcon,
  ClockIcon,
  PlusIcon,
  LockClosedIcon,
  MapPinIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import ForumPost from './ForumPost';

interface Topic {
  id: string;
  title: string;
  content: string;
  isPinned: boolean;
  isLocked: boolean;
  views: number;
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  category: {
    id: string;
    name: string;
    forum: {
      id: string;
      name: string;
      slug: string;
    };
  };
  _count: {
    posts: number;
  };
}

interface Post {
  id: string;
  content: string;
  isEdited: boolean;
  editedAt?: string;
  createdAt: string;
  author: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  reactions: Reaction[];
}

interface Reaction {
  id: string;
  emoji: string;
  user: {
    id: string;
    username: string;
  };
}

const ForumTopicPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCurrentUser();
    fetchTopic();
    fetchPosts();
  }, [topicId, page]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setCurrentUserId(response.data.id);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchTopic = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/forums/topics/${topicId}`);
      setTopic(response.data);
    } catch (error) {
      console.error('Error fetching topic:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(
        `/api/forums/topics/${topicId}/posts`,
        {
          params: { page, limit: 20 },
        }
      );
      setPosts(response.data.posts);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || submitting) return;

    try {
      setSubmitting(true);
      const response = await axios.post(
        `/api/forums/topics/${topicId}/posts`,
        {
          content: replyContent,
        }
      );
      setPosts([...posts, response.data]);
      setReplyContent('');
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPost = async (postId: string, content: string) => {
    try {
      const response = await axios.put(`/api/forums/posts/${postId}`, {
        content,
      });
      setPosts(posts.map((p) => (p.id === postId ? response.data : p)));
    } catch (error) {
      console.error('Error editing post:', error);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`/api/forums/posts/${postId}`);
      setPosts(posts.filter((p) => p.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleReaction = async (postId: string, emoji: string) => {
    try {
      const post = posts.find((p) => p.id === postId);
      const existingReaction = post?.reactions.find(
        (r) => r.user.id === currentUserId && r.emoji === emoji
      );

      if (existingReaction) {
        await axios.delete(`/api/forums/posts/${postId}/reactions`, {
          data: { emoji },
        });
      } else {
        await axios.post(`/api/forums/posts/${postId}/reactions`, { emoji });
      }

      // Refresh posts to get updated reactions
      fetchPosts();
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
  };

  if (loading || !topic) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <button
          onClick={() =>
            navigate(`/forums/${topic.category.forum.slug}`)
          }
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to {topic.category.forum.name}
        </button>
        <div className="text-sm text-gray-500 mt-2">
          {topic.category.forum.name} / {topic.category.name}
        </div>
      </div>

      {/* Topic Header */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <img
              src={
                topic.author.avatar ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  topic.author.firstName + ' ' + topic.author.lastName
                )}`
              }
              alt={topic.author.username}
              className="h-12 w-12 rounded-full"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {topic.title}
                </h1>
                {topic.isPinned && (
                  <MapPinIcon className="h-5 w-5 text-blue-600" />
                )}
                {topic.isLocked && (
                  <LockClosedIcon className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>
                  by {topic.author.firstName} {topic.author.lastName}
                </span>
                <span className="flex items-center gap-1">
                  <ClockIcon className="h-4 w-4" />
                  {new Date(topic.createdAt).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <EyeIcon className="h-4 w-4" />
                  {topic.views} views
                </span>
                <span className="flex items-center gap-1">
                  <ChatBubbleLeftIcon className="h-4 w-4" />
                  {topic._count.posts} replies
                </span>
              </div>
              <div className="mt-4 prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {topic.content}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts */}
      <div className="space-y-4 mb-6">
        {posts.map((post, index) => (
          <ForumPost
            key={post.id}
            post={post}
            currentUserId={currentUserId}
            onEdit={handleEditPost}
            onDelete={handleDeletePost}
            onReaction={handleReaction}
            index={index + 1}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mb-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2 bg-white border border-gray-300 rounded-lg">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Reply Form */}
      {!topic.isLocked && currentUserId && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Post a Reply
          </h3>
          <form onSubmit={handleSubmitReply}>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write your reply..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={4}
              disabled={submitting}
            />
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={!replyContent.trim() || submitting}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Posting...' : 'Post Reply'}
              </button>
            </div>
          </form>
        </div>
      )}

      {topic.isLocked && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <LockClosedIcon className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
          <p className="text-yellow-800">
            This topic is locked. No new replies can be added.
          </p>
        </div>
      )}

      {!currentUserId && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Sign in to reply to this topic
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      )}
    </div>
  );
};

export default ForumTopicPage;
